import { Auth0Client } from '@auth0/auth0-spa-js';
import { getAuth0LoginHint } from './login-hint';

/**
 * Auth0 SDK integration, parallel to `msal.ts`.
 *
 * The shell supports Auth0 and MSAL side by side (PRTL-2857): the shell's own
 * session stays on MSAL, and Auth0 provides per-MFE tokens (and global SSO) for
 * the MFEs listed in `VITE_AUTH0_MFE_IDS`. Auth0 is disabled entirely when
 * `VITE_AUTH0_CLIENT_ID` is empty — no client is constructed and every code path
 * remains MSAL-only.
 *
 * Custom-claim enrichment (`user-roles`, `clientId`) is injected by an Auth0
 * Action keyed off the `paramclientid` query param on `/authorize`. A
 * `refresh_token` grant does NOT re-run that Action, so `useRefreshTokens` is
 * kept `false` and silent renewal goes through the hidden-iframe `/authorize`
 * flow. We keep the SDK cache in memory and rehydrate per-tab sessions via a
 * silent Auth0 SSO check (`getTokenSilently`) when needed.
 */

export interface Auth0Config {
  domain: string;
  clientId: string;
  audience: string;
  redirectUri: string;
  postLogoutRedirectUri: string;
  /** Uppercased app IDs that require Auth0 tokens (from VITE_AUTH0_MFE_IDS). */
  mfeIds: Set<string>;
}

/**
 * Trim an env value and treat an unreplaced deploy token (`#{VAR}#`, shipped
 * when a pipeline variable is missing) as empty, so a missing prod variable
 * never yields a broken domain/clientId that would try to initialize Auth0.
 */
function sanitizeEnv(value: string | undefined): string {
  let s = (value ?? '').trim();
  // The deploy step substitutes a pipeline `value:` verbatim into the
  // `"#{VAR}#"` token, so a yml value written with surrounding quotes (e.g.
  // `value: 'tenant.auth0.com'`) leaks literal quotes into the domain/clientId/
  // audience/redirect URI (and makes an intended-empty `''` non-empty). Strip
  // one layer of matching surrounding quotes defensively.
  if (
    s.length >= 2 &&
    ((s.startsWith("'") && s.endsWith("'")) || (s.startsWith('"') && s.endsWith('"')))
  ) {
    s = s.slice(1, -1).trim();
  }
  if (!s || s.startsWith('#{')) return '';
  return s;
}

/** Resolve Auth0 configuration from env (no value is hardcoded in source). */
export function getAuth0Config(): Auth0Config {
  const origin = window.location.origin;
  const mfeIds = new Set(
    sanitizeEnv(import.meta.env.VITE_AUTH0_MFE_IDS)
      .split(',')
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean),
  );
  return {
    domain: sanitizeEnv(import.meta.env.VITE_AUTH0_DOMAIN),
    clientId: sanitizeEnv(import.meta.env.VITE_AUTH0_CLIENT_ID),
    audience: sanitizeEnv(import.meta.env.VITE_AUTH0_AUDIENCE),
    redirectUri: sanitizeEnv(import.meta.env.VITE_AUTH0_REDIRECT_URI) || `${origin}/home`,
    postLogoutRedirectUri:
      sanitizeEnv(import.meta.env.VITE_AUTH0_POST_LOGOUT_REDIRECT_URI) || `${origin}/login`,
    mfeIds,
  };
}

/** True when Auth0 is configured (client id + domain present). */
export function isAuth0Configured(): boolean {
  const cfg = getAuth0Config();
  return Boolean(cfg.clientId && cfg.domain);
}

/**
 * The Auth0 SDK client, or `null` when Auth0 is not configured. Exported as a
 * live binding so consumers see it once `initializeAuth0()` has run.
 */
export let auth0Client: Auth0Client | null = null;

/**
 * Resolve the active clientId for Auth0 flows WITHOUT importing the client
 * store (that would create an `auth0 → client-store → auth-module → auth0`
 * cycle): pathname (`/{clientId}/app-container/...`) → sessionStorage →
 * localStorage. Mirrors `resolveClientIdStrict` but with storage-only sources.
 */
function resolveClientIdForAuth0(): string | null {
  const parts = window.location.pathname.split('/').filter(Boolean);
  if (parts.length >= 3 && parts[1] === 'app-container' && parts[0]) return parts[0];
  try {
    const s = sessionStorage.getItem('currentlySelectedClientId')?.trim();
    if (s) return s;
  } catch {
    /* storage unavailable */
  }
  try {
    const l = localStorage.getItem('currentlySelectedClientId')?.trim();
    if (l) return l;
  } catch {
    /* storage unavailable */
  }
  return null;
}

let fetchPatched = false;
let globalSessionVerified = false;
let silentSessionCheckAttempted = false;

function shouldPatchTokenRequest(url: string, method: string): boolean {
  if (method.toUpperCase() !== 'POST') return false;
  const { domain } = getAuth0Config();
  if (!domain) return false;
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.hostname === domain && parsed.pathname === '/oauth/token';
  } catch {
    return false;
  }
}

/**
 * Inject `paramclientid` into the Auth0 `/oauth/token` exchange body (D10).
 *
 * auth0-spa-js forwards custom `authorizationParams` (like `paramclientid`)
 * ONLY on the `/authorize` request, NOT on the subsequent `/oauth/token`
 * code exchange. The tenant Action that enriches `user-roles`/`clientId` keys
 * off `paramclientid`, so without this patch the resulting access token drops
 * those claims. We wrap `window.fetch` and add `paramclientid` (if absent) to
 * POSTs targeting exactly `{VITE_AUTH0_DOMAIN}/oauth/token`; every other request
 * passes through untouched.
 */
function patchAuth0TokenFetch(): void {
  if (fetchPatched || typeof window === 'undefined') return;
  const nativeFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url =
      typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
    const method =
      init?.method ?? (typeof input === 'string' || input instanceof URL ? 'GET' : input.method);

    if (!shouldPatchTokenRequest(url, method)) return nativeFetch(input, init);

    const clientId = resolveClientIdForAuth0();
    if (!clientId) return nativeFetch(input, init);

    const body = init?.body;
    if (typeof body === 'string') {
      const params = new URLSearchParams(body);
      if (!params.has('paramclientid')) params.set('paramclientid', clientId);
      return nativeFetch(input, { ...init, body: params.toString() });
    }
    if (body instanceof URLSearchParams) {
      const params = new URLSearchParams(body.toString());
      if (!params.has('paramclientid')) params.set('paramclientid', clientId);
      return nativeFetch(input, { ...init, body: params });
    }
    if (body instanceof FormData) {
      if (!body.has('paramclientid')) body.set('paramclientid', clientId);
      return nativeFetch(input, { ...init, body });
    }
    return nativeFetch(input, init);
  };
  fetchPatched = true;
}

const USER_CONTEXT_KEY = 'ey.user.context';
const AUTH_PROVIDER_STORAGE_KEY = 'authProvider';
const asStr = (v: unknown): string => (typeof v === 'string' ? v : '');

/**
 * Persist Auth0 identity in the MSAL-shaped `ey.user.context` that legacy MFEs
 * and `legacy-user-bridge` read. Shared by the callback and the boot-time
 * session restore so both write an identical shape (PRTL-2857 1a).
 */
export function writeAuth0UserContext(claims: Record<string, unknown>): void {
  const email = asStr(claims.email) || asStr(claims.preferred_username);
  const context = {
    idToken: asStr(claims.__raw),
    localAccountId: asStr(claims.oid),
    name: asStr(claims.name),
    username: email,
    tenantId: asStr(claims.tid),
    idTokenClaims: {
      sub: asStr(claims.sub),
      oid: asStr(claims.oid),
      email,
      preferred_username: asStr(claims.preferred_username) || email,
      name: asStr(claims.name),
      iss: asStr(claims.iss),
    },
  };
  try {
    sessionStorage.setItem(USER_CONTEXT_KEY, JSON.stringify(context));
  } catch {
    /* private mode */
  }
}

/**
 * Rehydrate identity for an EXISTING Auth0 session at boot (independent shell
 * mode) — a new tab or hard refresh restores the session via a silent Auth0
 * SSO check without ever running the callback. When a session is present this rewrites
 * `ey.user.context` and marks `authProvider = 'auth0'` so the user bridge and
 * legacy MFEs have identity without another redirect.
 */
export async function restoreAuth0SessionContext(): Promise<void> {
  if (!auth0Client) return;
  try {
    if (!(await isAuth0Authenticated())) return;
    const claims = await auth0Client.getIdTokenClaims();
    if (claims) writeAuth0UserContext(claims as unknown as Record<string, unknown>);
    try {
      localStorage.setItem(AUTH_PROVIDER_STORAGE_KEY, 'auth0');
    } catch {
      /* storage unavailable */
    }
  } catch {
    /* best-effort — a restore miss just means the guard redirects to login */
  }
}

/**
 * Construct the Auth0 client at bootstrap when configured. Patches the token
 * fetch first so the `/oauth/token` exchange carries `paramclientid`. In the
 * independent Auth0 shell mode (VITE_AUTH_PROVIDER=auth0) it then rehydrates an
 * existing session's identity. Session ESTABLISHMENT is deferred to the guard
 * (`requireAuth` → Auth0 auto-redirect in shell mode; `ensureGlobalAuth0Session`
 * after MSAL login in overlay mode) and, as a fallback, `ensureAuth0ForMfe` — so
 * no redirect fires at boot for MSAL-only users.
 */
export async function initializeAuth0(): Promise<void> {
  if (!isAuth0Configured()) return;
  patchAuth0TokenFetch();
  silentSessionCheckAttempted = false;
  const cfg = getAuth0Config();
  const initialClientId = resolveClientIdForAuth0();
  auth0Client = new Auth0Client({
    domain: cfg.domain,
    clientId: cfg.clientId,
    authorizationParams: {
      redirect_uri: cfg.redirectUri,
      audience: cfg.audience,
      ...(initialClientId ? { paramclientid: initialClientId } : {}),
    },
    cacheLocation: 'memory',
    useRefreshTokens: false,
  });
  if (sanitizeEnv(import.meta.env.VITE_AUTH_PROVIDER).toLowerCase() === 'auth0') {
    await restoreAuth0SessionContext();
  }
}

/**
 * Check whether an Auth0 session is active. With `cacheLocation: 'memory'`,
 * duplicated/new tabs start with an empty SDK cache, so we run one silent SSO
 * probe (`getTokenSilently`) per page load before concluding "not authenticated".
 */
async function hasActiveAuth0Session(): Promise<boolean> {
  if (!auth0Client) return false;
  try {
    if (await auth0Client.isAuthenticated()) return true;
  } catch {
    /* continue to silent probe */
  }
  if (silentSessionCheckAttempted) return false;
  silentSessionCheckAttempted = true;

  const { audience } = getAuth0Config();
  const clientId = resolveClientIdForAuth0();
  try {
    await auth0Client.getTokenSilently({
      authorizationParams: {
        audience,
        ...(clientId ? { paramclientid: clientId } : {}),
      },
    });
    return await auth0Client.isAuthenticated();
  } catch {
    return false;
  }
}

/**
 * Prime the GLOBAL Auth0 SSO session right after MSAL login (PRTL-2857).
 *
 * Runs once per page-load (guarded by `globalSessionVerified`): if an Auth0
 * session already exists it just records `authProvider = 'auth0'`; otherwise it
 * starts an interactive `loginWithRedirect` (audience + redirect_uri +
 * paramclientid + login_hint, preserving `returnTo`) and returns `'redirecting'`
 * so the caller halts the current navigation while the full-page redirect takes
 * over. No-op when Auth0 is not configured, so MSAL-only flows are untouched.
 */
export async function ensureGlobalAuth0Session(returnTo: string): Promise<'ok' | 'redirecting'> {
  if (!isAuth0Configured() || globalSessionVerified) return 'ok';
  if (!auth0Client) return 'ok';

  const authenticated = await hasActiveAuth0Session();
  if (authenticated) {
    globalSessionVerified = true;
    try {
      localStorage.setItem('authProvider', 'auth0');
    } catch {
      /* storage unavailable */
    }
    return 'ok';
  }

  const cfg = getAuth0Config();
  const clientId = resolveClientIdForAuth0();
  const loginHint = getAuth0LoginHint();
  await auth0Client.loginWithRedirect({
    authorizationParams: {
      audience: cfg.audience,
      redirect_uri: cfg.redirectUri,
      ...(clientId ? { paramclientid: clientId } : {}),
      ...(loginHint ? { login_hint: loginHint } : {}),
    },
    appState: { returnTo },
  });
  return 'redirecting';
}

/** Whether an Auth0 session is currently active (best-effort, never throws). */
export async function isAuth0Authenticated(): Promise<boolean> {
  return hasActiveAuth0Session();
}

/**
 * Acquire an Auth0 access token. Passes `audience` and, when resolved,
 * `paramclientid` so the tenant Action enriches the token with
 * `user-roles`/`clientId`. `paramclientid` is optional because the independent
 * Auth0 shell's initial client-list fetch has no client yet (a user-scoped
 * token still authorizes it, and the `/oauth/token` fetch patch backfills
 * `paramclientid` from the active route once a client is selected). Routine
 * calls use the SDK's default caching; only a client switch passes `forceFresh`
 * (→ `cacheMode: 'off'`) to rebind `paramclientid`.
 */
export async function getAuth0Token(opts: {
  paramclientid?: string;
  forceFresh?: boolean;
}): Promise<string> {
  if (!auth0Client) throw new Error('Auth0 is not initialized.');
  const { audience } = getAuth0Config();
  return auth0Client.getTokenSilently({
    authorizationParams: {
      audience,
      ...(opts.paramclientid ? { paramclientid: opts.paramclientid } : {}),
    },
    ...(opts.forceFresh ? { cacheMode: 'off' as const } : {}),
  });
}

/**
 * Start an interactive Auth0 login redirect. Passes `audience`, `redirect_uri`,
 * `paramclientid` (for claim enrichment) and an optional `login_hint` (to skip
 * the account picker when a federated session exists — PRTL-2856). `returnTo`
 * is preserved in `appState` so the callback returns the user to the
 * originating route.
 */
export async function loginToAuth0(opts: {
  paramclientid?: string;
  returnTo?: string;
  loginHint?: string;
}): Promise<void> {
  if (!auth0Client) throw new Error('Auth0 is not initialized.');
  const { audience, redirectUri } = getAuth0Config();
  await auth0Client.loginWithRedirect({
    authorizationParams: {
      audience,
      redirect_uri: redirectUri,
      ...(opts.paramclientid ? { paramclientid: opts.paramclientid } : {}),
      ...(opts.loginHint ? { login_hint: opts.loginHint } : {}),
    },
    appState: {
      returnTo: opts.returnTo ?? `${window.location.pathname}${window.location.search}`,
    },
  });
}

/** Sign out of Auth0, returning to the configured post-logout URL. */
export async function logoutAuth0(): Promise<void> {
  if (!auth0Client) return;
  const { postLogoutRedirectUri } = getAuth0Config();
  await auth0Client.logout({ logoutParams: { returnTo: postLogoutRedirectUri } });
}

/**
 * Clear the Auth0 SDK's local session WITHOUT navigating (`openUrl: false`).
 * Used by the shell's unified logout, where MSAL `logoutRedirect` is the single
 * navigation and we only need Auth0's local cache cleared here.
 */
export async function clearAuth0Session(): Promise<void> {
  if (!auth0Client) return;
  try {
    await auth0Client.logout({ openUrl: false });
  } catch {
    /* best-effort local cleanup */
  }
}
